import { useEffect, useMemo, useState } from 'react';

import { convertToRaw } from 'draft-js';
import { observer } from 'mobx-react-lite';

import { Avatar } from '@components/avatar';
import { Button } from '@components/button';
import { IconEdit } from '@components/icons/edit';
import { IconInfoSmall } from '@components/icons/info-small';
import { IconLock } from '@components/icons/lock';
import { IconMoreVertical } from '@components/icons/more-vertical';
import { IconTrash } from '@components/icons/trash';
import { Menu } from '@components/menu';
import { Stack } from '@components/stack';
import { Switch } from '@components/switch';
import { Text } from '@components/text';
import { TextEditor, useEditorState } from '@components/text-editor';
import { useIsMobile } from '@hooks/use-is-mobile';
import { makeEditorState } from '@utils/draftjs';

import { Box } from 'components/Box';
import { TextEditorToolbar } from 'components/TextEditorToolbar';
import { useAuthenticatedUser } from 'features/authentication';
import { useModal } from 'features/modals';
import { useStore } from 'models/Provider';
import { IApplication, IApplicationNote, IJob, IUser } from 'models/types';

export const ApplicationNotes = observer(({ application }: { application: IApplication }) => {
  const [editing, setEditing] = useState<IApplicationNote>({});
  const { t } = useStore();
  const job = application.job;

  useEffect(() => {
    application.setUserRead('notes');
  }, [application]);

  return (
    <Box pt="24" pb="24">
      <Stack space={4}>
        <Text font="bold14" text={t('application.notes.header')} />

        <div>
          <NotesTextBox application={application} disabled={!!editing.id} t={t} />

          {application.chat.map((note: IApplicationNote) => (
            <ApplicationNote
              key={note.id}
              note={note}
              setEditing={setEditing}
              editing={editing}
              job={job}
            />
          ))}
        </div>
      </Stack>
    </Box>
  );
});

const ApplicationNote = observer(
  ({
    note,
    setEditing,
    editing,
    job,
  }: {
    note: IApplicationNote;
    setEditing: (note: IApplicationNote) => void;
    editing: IApplicationNote;
    job: IJob;
  }) => {
    const { t } = useStore();
    const { showModal } = useModal();
    const me = useAuthenticatedUser();
    const isMobile = useIsMobile();

    function removeModal(note: IApplicationNote) {
      showModal('COMMON_REMOVE_CONFIRM', {
        _container: {
          minWidth: '200px',
          autoWidth: true,
        },
        data: { id: note.id, confirmText: t('modals.removeconfirm.note.confirm') },
        confirm: note.remove,
      });
    }

    function editNote(note: IApplicationNote) {
      setEditing(note);
    }

    function cancelEdit() {
      setEditing({});
    }

    const canEditNote = editing.id !== note.id && note.user?.id === me.id && !isMobile;
    const canDeleteNote =
      editing.id !== note.id && (note.user?.id === me.id || me.role === 'superadmin') && !isMobile;

    return (
      <Box flex justifyContent="flex-end" mt="24">
        <Box style={{ width: isMobile || editing.id === note.id ? '100%' : 'auto' }}>
          <Box flex justifyContent="flex-end" grow>
            {(canEditNote || canDeleteNote) && (
              <Box mr="12">
                <Menu buttonProps={{ variant: 'ghost', iconLeft: <IconMoreVertical /> }}>
                  {canEditNote && (
                    <Menu.Item
                      onSelect={() => editNote(note)}
                      icon={<IconEdit />}
                      label={t('application.notes.actions.edit')}
                    />
                  )}
                  {canDeleteNote && (
                    <Menu.Item
                      variant="danger"
                      icon={<IconTrash />}
                      onSelect={() => removeModal(note)}
                      label={t('application.notes.actions.delete')}
                    />
                  )}
                </Menu>
              </Box>
            )}
            {editing.id === note.id ? (
              <NoteEdit t={t} key={note.text} note={editing} job={job} onCancel={cancelEdit} />
            ) : (
              <Note key={note.text} note={note} />
            )}
          </Box>
          <Box flex mt={4} justifyContent="flex-end" alignItems="center">
            {note.isAdminNote && (
              <Box flex alignItems="center" mr={8}>
                <IconLock
                  color="purple50"
                  tooltip={t('application.notes.textbox.admin_note.label')}
                />
              </Box>
            )}
            <Stack space={2} orientation="horizontal">
              {isMobile && <Avatar src={note.user.picture} name={note.user.name} />}

              <Text
                font="regular14"
                color="purple60"
                style={{ textAlign: 'right' }}
                text={note.dateDisplay}
              />
            </Stack>
          </Box>
        </Box>
        {!isMobile && (
          <Box ml="16">
            <Avatar src={note.user.picture} name={note.user.name} size="lg" />
          </Box>
        )}
      </Box>
    );
  }
);

const Note = observer(({ note }: { note: IApplicationNote }) => {
  const { userStore } = useStore();
  return (
    <TextEditor
      key={note.text}
      variant="orange"
      readOnly
      initialEditorState={makeEditorState(note.text)}
    >
      <TextEditorToolbar />
      <TextEditor.Mention getUser={(id) => userStore.items.get(id)} />
      <TextEditor.Input />
    </TextEditor>
  );
});

const NotesTextBox = observer(
  ({
    application,
    disabled,
    t,
  }: {
    application: IApplication;
    disabled: boolean;
    t: (arg0: string, ...args: string[]) => string;
  }) => {
    const { userStore } = useStore();
    const job = application.job;
    const [adding, setAdding] = useState(false);
    const [isAdminNote, setisAdminNote] = useState(false);
    const { editorState, resetEditorState, setEditorState, isEmpty } = useEditorState('');
    const me = useAuthenticatedUser();

    const followers = useMemo(() => {
      let followers = [...job.activeFollowers];
      application.followers.forEach((af: IUser) => followers.push(af));
      if (isAdminNote) {
        followers = followers.filter((f) => f.role === 'admin');
      }
      return followers;
    }, [application.followers, job.activeFollowers, isAdminNote]);

    const saveNote = async () => {
      setAdding(true);
      const raw = convertToRaw(editorState.getCurrentContent());
      const success = await application.addNote(JSON.stringify(raw), isAdminNote);
      if (success) {
        resetEditorState();
        setisAdminNote(false);
      }
      setAdding(false);
    };

    return (
      <Box grow>
        <TextEditor
          variant="orange"
          disabled={disabled}
          onChange={setEditorState}
          editorState={editorState}
        >
          <TextEditorToolbar />
          <TextEditor.Input
            placeholder={
              isAdminNote
                ? t('application.notes.textbox.placeholder.admin')
                : t('application.notes.textbox.placeholder.regular')
            }
          />
          <TextEditor.Mention suggestions={followers} getUser={(id) => userStore.items.get(id)} />
          <TextEditor.Actions>
            {(me.role === 'admin' || me.role === 'superadmin') && (
              <Stack space={2} orientation="horizontal" align="center" style={{ flexGrow: 1 }}>
                <Switch
                  value={isAdminNote}
                  onChange={() => setisAdminNote(!isAdminNote)}
                  label={t('application.notes.textbox.switch.admin')}
                />
                <IconInfoSmall color="purple50" tooltip={t('application.notes.textbox.info')} />
              </Stack>
            )}
            <Button
              variant="primary"
              onClick={saveNote}
              disabled={adding || isEmpty}
              text={
                isAdminNote
                  ? t('application.notes.textbox.button.submit.admin')
                  : t('application.notes.textbox.button.submit.regular')
              }
            />
          </TextEditor.Actions>
        </TextEditor>
      </Box>
    );
  }
);

const NoteEdit = observer(
  ({
    note,
    onCancel,
    job,
    t,
  }: {
    note: IApplicationNote;
    onCancel: () => void;
    job: IJob;
    t: (arg0: string, ...args: string[]) => string;
  }) => {
    const { userStore } = useStore();
    const [adding, setAdding] = useState(false);
    const { editorState, setEditorState, isEmpty } = useEditorState(note.text);

    useEffect(() => {
      note.edit();
    }, [note]);

    const suggestions = useMemo(() => {
      let followers = [...job.activeFollowers];
      if (note.isAdminNote) {
        followers = followers.filter((f) => f.role === 'admin');
      }
      return followers;
    }, [job.activeFollowers, note.isAdminNote]);

    async function updateNote() {
      setAdding(true);
      const text = JSON.stringify(convertToRaw(editorState.getCurrentContent()));
      const success = await note.update(text);
      success && onCancel();
    }

    return (
      <TextEditor focusOnMount variant="orange" editorState={editorState} onChange={setEditorState}>
        <TextEditorToolbar />
        <TextEditor.Mention
          editMode
          suggestions={suggestions}
          getUser={(id) => userStore.items.get(id)}
        />
        <TextEditor.Input />
        <TextEditor.Actions>
          <Button
            variant="secondary"
            onClick={onCancel}
            text={t('application.notes.edit.button.cancel')}
          />
          <Button
            style={{ marginLeft: 8 }}
            onClick={updateNote}
            isDisabled={isEmpty || adding}
            text={t('application.notes.edit.button.submit')}
          />
        </TextEditor.Actions>
      </TextEditor>
    );
  }
);
